import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle, ArrowLeft, Calendar, ChevronRight, Cloud, Download, FileText,
  Loader2, RefreshCw, TrendingDown, TrendingUp, Users, Zap,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAROL_COLOR = {
  verde: { bg: "bg-emerald-500", text: "text-emerald-700", soft: "bg-emerald-50", border: "border-emerald-200", label: "Verde" },
  amarelo: { bg: "bg-amber-500", text: "text-amber-700", soft: "bg-amber-50", border: "border-amber-200", label: "Amarelo" },
  vermelho: { bg: "bg-red-500", text: "text-red-700", soft: "bg-red-50", border: "border-red-200", label: "Vermelho" },
};

const TIPO_IMPREVISTO_LABEL = {
  chuva: "Chuva", falta_colaborador: "Falta de Colaborador",
  atraso_material: "Atraso de Material", problema_estrutural: "Problema Estrutural",
  interdicao: "Interdição", greve: "Greve",
  acidente: "Acidente de Trabalho", outro: "Outro",
};

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));
const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s + "T00:00:00").toLocaleDateString("pt-BR"); } catch { return s; }
};

export default function CronogramasDashboardPage() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [incluirFinalizados, setIncluirFinalizados] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cronogramas-dashboard/executivo?incluir_finalizados=${incluirFinalizados}`, { headers });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar dashboard");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incluirFinalizados]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center min-h-[400px] text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Carregando dashboard executivo...
        </div>
      </MainLayout>
    );
  }

  const kpis = data.kpis_gerais;

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <button
              onClick={() => navigate("/operacional/cronogramas")}
              className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-1"
              data-testid="btn-voltar-cronogramas"
            >
              <ArrowLeft className="h-3 w-3" /> Voltar para Cronogramas
            </button>
            <h1 className="text-2xl font-bold text-luvcorp-navy" data-testid="dashboard-executivo-title">
              Dashboard Executivo · Portfólio de Obras
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Visão consolidada de todas as obras em andamento — referência {data.mes_referencia}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={incluirFinalizados}
                onChange={(e) => setIncluirFinalizados(e.target.checked)}
                data-testid="checkbox-incluir-finalizados"
                className="accent-luvcorp-navy"
              />
              <span className="text-slate-600">Incluir finalizadas</span>
            </label>
            <Button size="sm" variant="outline" onClick={fetchData} data-testid="btn-refresh-dashboard">
              <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
            </Button>
          </div>
        </div>

        {/* Stoplight Big Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FarolBigCard farol="verde" count={kpis.obras_por_farol.verde} total={kpis.total_obras} subtitle="Dentro do prazo" />
          <FarolBigCard farol="amarelo" count={kpis.obras_por_farol.amarelo} total={kpis.total_obras} subtitle="Desvio entre 5% e 10%" />
          <FarolBigCard farol="vermelho" count={kpis.obras_por_farol.vermelho} total={kpis.total_obras} subtitle="Desvio &gt; 10% ou caminho crítico atrasado" />
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCell icon={Calendar} label="Total de Obras" value={kpis.total_obras} accent="navy" />
          <KpiCell icon={TrendingUp} label="Valor Total" value={fmtBRL(kpis.valor_total_obras)} accent="navy" small />
          <KpiCell icon={Zap} label="Progresso Médio" value={`${kpis.progresso_medio_pct}%`} accent="emerald" />
          <KpiCell icon={TrendingDown} label="Dias Perdidos" value={kpis.total_dias_perdidos} accent="red" />
          <KpiCell icon={AlertTriangle} label="Imprevistos no Mês" value={kpis.total_imprevistos_mes} accent="amber" />
        </div>

        {/* Two-column: Obras list + Side panels */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Obras List */}
          <Card className="border-0 shadow-sm lg:col-span-2" data-testid="card-obras-portfolio">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-luvcorp-navy flex items-center gap-2">
                <Calendar className="h-5 w-5" /> Obras em Andamento
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Ordenadas por severidade · Clique para abrir o cronograma da obra.
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.obras.length === 0 ? (
                <p className="text-center text-slate-400 py-8 text-sm">Nenhuma obra cadastrada.</p>
              ) : (
                data.obras.map((o) => (
                  <ObraRow
                    key={o.id}
                    obra={o}
                    onClick={() => navigate(`/operacional/cronogramas?openId=${o.id}`)}
                  />
                ))
              )}
            </CardContent>
          </Card>

          {/* Side Panels */}
          <div className="space-y-6">
            <RankingAtraso ranking={data.ranking_atraso} onClick={(id) => navigate(`/operacional/cronogramas?openId=${id}`)} />
            <ImprevistosPorTipo imprevistos={data.imprevistos_por_tipo} />
            <ImprevistosPorObra imprevistos={data.imprevistos_por_obra} onClick={(id) => navigate(`/operacional/cronogramas?openId=${id}`)} />
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

function FarolBigCard({ farol, count, total, subtitle }) {
  const cfg = FAROL_COLOR[farol];
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Card className={`border-0 shadow-sm ${cfg.soft} ${cfg.border} border`} data-testid={`farol-${farol}`}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-14 h-14 rounded-full ${cfg.bg} flex items-center justify-center shadow-lg`}>
          <div className="w-3 h-3 rounded-full bg-white animate-pulse" />
        </div>
        <div className="flex-1">
          <p className={`text-xs uppercase tracking-wide font-bold ${cfg.text}`}>Farol {cfg.label}</p>
          <p className="text-3xl font-bold text-slate-800 mt-0.5">
            {count}
            <span className="text-sm text-slate-400 font-normal ml-1">/ {total} obras</span>
          </p>
          <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
        </div>
        <div className="text-right">
          <p className={`text-2xl font-bold ${cfg.text}`}>{pct}%</p>
        </div>
      </CardContent>
    </Card>
  );
}

function KpiCell({ icon: Icon, label, value, accent, small }) {
  const accents = {
    navy: "text-luvcorp-navy",
    emerald: "text-emerald-600",
    red: "text-red-600",
    amber: "text-amber-600",
  };
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold leading-tight">{label}</p>
          <Icon className={`h-4 w-4 ${accents[accent]}`} />
        </div>
        <p className={`${small ? "text-base" : "text-2xl"} font-bold mt-1 ${accents[accent]}`} data-testid={`kpi-${label}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function ObraRow({ obra, onClick }) {
  const cfg = FAROL_COLOR[obra.farol];
  const pctConclusao = obra.total_etapas > 0 ? Math.round((obra.etapas_concluidas / obra.total_etapas) * 100) : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left border rounded-lg p-3 hover:shadow-md transition-all ${cfg.border} ${cfg.soft}`}
      data-testid={`obra-row-${obra.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-3 h-3 rounded-full ${cfg.bg} mt-1 shrink-0 shadow-sm`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 truncate">{obra.contrato_titulo}</span>
            {obra.caminho_critico_atrasado && (
              <Badge variant="outline" className="text-[10px] border-red-300 bg-red-100 text-red-700">
                Caminho Crítico Atrasado
              </Badge>
            )}
            {obra.status_cronograma === "finalizado" && (
              <Badge variant="outline" className="text-[10px]">Finalizado</Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{obra.cliente_nome || "—"} · {obra.endereco_obra || "—"}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
            <div>
              <p className="text-slate-400 text-[10px] uppercase tracking-wide">Progresso</p>
              <p className="font-semibold text-slate-700">{obra.pct_progresso_fisico}%</p>
            </div>
            <div>
              <p className="text-slate-400 text-[10px] uppercase tracking-wide">Desvio</p>
              <p className={`font-semibold ${obra.desvio_pct > 10 ? "text-red-600" : obra.desvio_pct >= 5 ? "text-amber-600" : "text-emerald-600"}`}>
                {obra.desvio_pct.toFixed(1)}%
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-[10px] uppercase tracking-wide">Dias Perdidos</p>
              <p className="font-semibold text-slate-700">{obra.dias_perdidos_total}d</p>
            </div>
            <div>
              <p className="text-slate-400 text-[10px] uppercase tracking-wide">Conclusão</p>
              <p className="font-semibold text-slate-700">{fmtDate(obra.data_conclusao_projetada)}</p>
            </div>
          </div>

          {obra.proxima_etapa && (
            <p className="text-xs text-slate-500 mt-2 truncate">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">Próx. Etapa:</span>{" "}
              <b className="text-slate-700">{obra.proxima_etapa}</b> ({obra.etapas_concluidas}/{obra.total_etapas} etapas · {pctConclusao}%)
            </p>
          )}
        </div>
        <ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-1" />
      </div>
    </button>
  );
}

function RankingAtraso({ ranking, onClick }) {
  return (
    <Card className="border-0 shadow-sm" data-testid="card-ranking-atraso">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-luvcorp-navy flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-red-500" /> Ranking de Atrasos
        </CardTitle>
        <p className="text-[11px] text-slate-500">Top obras com maior desvio %</p>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {ranking.length === 0 ? (
          <p className="text-xs text-slate-400 italic text-center py-3">Nenhuma obra com atraso 🎉</p>
        ) : (
          ranking.map((o, idx) => (
            <button
              key={o.id}
              onClick={() => onClick(o.id)}
              className="w-full flex items-center gap-2 p-2 hover:bg-slate-50 rounded text-left transition-colors"
              data-testid={`ranking-item-${idx}`}
            >
              <span className="text-xs font-bold text-slate-400 w-5">#{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">{o.contrato_titulo}</p>
                <p className="text-[10px] text-slate-500 truncate">{o.cliente_nome || "—"}</p>
              </div>
              <Badge variant="outline" className={`text-[10px] ${o.desvio_pct > 10 ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                +{o.desvio_pct.toFixed(1)}%
              </Badge>
            </button>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function ImprevistosPorTipo({ imprevistos }) {
  const maxDias = Math.max(...imprevistos.map(i => i.dias_perdidos), 1);
  return (
    <Card className="border-0 shadow-sm" data-testid="card-imprevistos-tipo">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-luvcorp-navy flex items-center gap-2">
          <Cloud className="h-4 w-4 text-amber-500" /> Imprevistos por Tipo (mês)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {imprevistos.length === 0 ? (
          <p className="text-xs text-slate-400 italic text-center py-3">Sem imprevistos este mês.</p>
        ) : (
          imprevistos.map((i) => (
            <div key={i.tipo} className="space-y-1" data-testid={`imprevisto-tipo-${i.tipo}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-slate-700">{TIPO_IMPREVISTO_LABEL[i.tipo] || i.tipo}</span>
                <span className="text-slate-500">
                  <b className="text-red-600">-{i.dias_perdidos}d</b> ({i.total}x)
                </span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-amber-400" style={{ width: `${(i.dias_perdidos / maxDias) * 100}%` }} />
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function ImprevistosPorObra({ imprevistos, onClick }) {
  return (
    <Card className="border-0 shadow-sm" data-testid="card-imprevistos-obra">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-luvcorp-navy flex items-center gap-2">
          <Users className="h-4 w-4 text-amber-500" /> Obras com Mais Imprevistos (mês)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {imprevistos.length === 0 ? (
          <p className="text-xs text-slate-400 italic text-center py-3">Sem imprevistos este mês.</p>
        ) : (
          imprevistos.map((o, idx) => (
            <button
              key={o.cronograma_id}
              onClick={() => onClick(o.cronograma_id)}
              className="w-full flex items-center gap-2 p-2 hover:bg-slate-50 rounded text-left transition-colors"
              data-testid={`imprevisto-obra-${idx}`}
            >
              <span className="text-xs font-bold text-slate-400 w-5">#{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">{o.obra_titulo}</p>
                <p className="text-[10px] text-slate-500 truncate">{o.total} {o.total === 1 ? "ocorrência" : "ocorrências"}</p>
              </div>
              <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-700">
                -{o.dias_perdidos}d
              </Badge>
            </button>
          ))
        )}
      </CardContent>
    </Card>
  );
}
